import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  FiBook, FiSearch, FiFilter, FiGrid, FiList,
  FiChevronLeft, FiChevronRight, FiX, FiExternalLink, FiGlobe,
  FiHome, FiLogIn, FiArrowLeft, FiDownload
} from 'react-icons/fi';
import './KatalogPublic.css';

const API_BASE = '/api';

const KatalogPublic = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    sort: searchParams.get('sort') || 'newest',
    availability: searchParams.get('availability') || ''
  });

  const fetchBooks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        kategori: filters.category,
        sort: filters.sort,
        availability: filters.availability
      });

      const response = await fetch(`${API_BASE}/public_books.php?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setBooks(data.data || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total_rows || 0,
          totalPages: data.pagination?.total_pages || 1
        }));
        if (data.filters?.kategoris) {
          setCategories(data.filters.kategoris);
        }
      }
    } catch (error) {
      console.error('Error fetching books:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  useEffect(() => {
    fetchBooks();
  }, [fetchBooks]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    updateSearchParams();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const updateSearchParams = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.category) params.set('category', filters.category);
    if (filters.sort) params.set('sort', filters.sort);
    if (filters.availability) params.set('availability', filters.availability);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      sort: 'newest',
      availability: ''
    });
    setSearchParams({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const hasActiveFilters = filters.category || filters.availability || filters.search;

  return (
    <div className="katalog-public">
      {/* Top Navigation */}
      <nav className="katalog-public__nav">
        <div className="katalog-public__nav-container">
          <Link to="/" className="katalog-public__logo">
            <FiBook className="katalog-public__logo-icon" />
            <span>STIKBAR E-Library</span>
          </Link>
          <div className="katalog-public__nav-actions">
            <Link to="/" className="katalog-public__nav-btn">
              <FiHome /> Beranda
            </Link>
            <Link to="/login" className="katalog-public__nav-btn katalog-public__nav-btn--primary">
              <FiLogIn /> Masuk
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="katalog-public__main">
        <div className="katalog-public__container">
          {/* Breadcrumb */}
          <div className="katalog-public__breadcrumb">
            <Link to="/">
              <FiArrowLeft /> Kembali ke Beranda
            </Link>
          </div>

          {/* Header */}
          <header className="katalog-public__header">
            <div className="katalog-public__header-left">
              <h1 className="katalog-public__title">Katalog Buku</h1>
              <p className="katalog-public__subtitle">
                {pagination.total} buku tersedia di perpustakaan
              </p>
            </div>

            <div className="katalog-public__header-actions">
              <form className="katalog-public__search" onSubmit={handleSearch}>
                <FiSearch className="katalog-public__search-icon" />
                <input
                  type="text"
                  placeholder="Cari judul, pengarang, ISBN..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
                {filters.search && (
                  <button 
                    type="button" 
                    className="katalog-public__search-clear"
                    onClick={() => handleFilterChange('search', '')}
                  >
                    <FiX />
                  </button>
                )}
              </form>

              <button 
                className={`katalog-public__filter-toggle ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FiFilter />
                <span>Filter</span>
                {hasActiveFilters && <span className="katalog-public__filter-badge"></span>}
              </button>

              <div className="katalog-public__view-toggle">
                <button 
                  className={viewMode === 'grid' ? 'active' : ''}
                  onClick={() => setViewMode('grid')}
                  title="Tampilan Grid"
                >
                  <FiGrid />
                </button>
                <button 
                  className={viewMode === 'list' ? 'active' : ''}
                  onClick={() => setViewMode('list')}
                  title="Tampilan List"
                >
                  <FiList />
                </button>
              </div>
            </div>
          </header>

          {/* Filters */}
          {showFilters && (
            <div className="katalog-public__filters">
              <div className="katalog-public__filter-group">
                <label>Kategori</label>
                <select 
                  value={filters.category}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <option value="">Semua Kategori</option>
                  {categories.map(cat => (
                    <option key={cat.id_kategori} value={cat.id_kategori}>
                      {cat.nama_kategori}
                    </option>
                  ))}
                </select>
              </div>

              <div className="katalog-public__filter-group">
                <label>Ketersediaan</label>
                <select 
                  value={filters.availability}
                  onChange={(e) => handleFilterChange('availability', e.target.value)}
                >
                  <option value="">Semua</option>
                  <option value="available">Ada Stok</option>
                  <option value="borrowed">Stok Habis</option>
                  <option value="openlibrary">Open Library</option>
                  <option value="free">Akses Gratis</option>
                </select>
              </div>

              <div className="katalog-public__filter-group">
                <label>Urutkan</label>
                <select 
                  value={filters.sort}
                  onChange={(e) => handleFilterChange('sort', e.target.value)}
                >
                  <option value="newest">Terbaru</option>
                  <option value="oldest">Terlama</option>
                  <option value="title_asc">Judul A-Z</option>
                  <option value="title_desc">Judul Z-A</option>
                  <option value="popular">Terpopuler</option>
                </select>
              </div>

              {hasActiveFilters && (
                <button className="katalog-public__filter-clear" onClick={clearFilters}>
                  <FiX /> Reset Filter
                </button>
              )}
            </div>
          )}

          {/* Content */}
          {loading ? (
            <div className="katalog-public__loading">
              <div className="katalog-public__loading-spinner"></div>
              <p>Memuat data...</p>
            </div>
          ) : books.length === 0 ? (
            <div className="katalog-public__empty">
              <FiBook className="katalog-public__empty-icon" />
              <h3>Tidak ada buku ditemukan</h3>
              <p>Coba ubah filter atau kata kunci pencarian Anda</p>
              {hasActiveFilters && (
                <button className="katalog-public__empty-btn" onClick={clearFilters}>
                  Reset Filter
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Books Grid/List */}
              <div className={`katalog-public__books katalog-public__books--${viewMode}`}>
                {books.map(book => (
                  <Link 
                    to={`/katalog-publik/${book.id_buku}`} 
                    key={book.id_buku}
                    className="katalog-public__book"
                  >
                    <div className="katalog-public__book-cover">
                      <img 
                        src={book.cover_image || '/images/cover-default.png'} 
                        alt={book.judul}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/cover-default.png';
                        }}
                      />
                      {/* Check if truly external (not dosen upload) */}
                      {(book.tipe_buku === 'external' || book.sumber_buku === 'Open Library' || book.sumber_buku === 'Google Books') && 
                       book.sumber_buku !== 'Upload Dosen' && !book.id_pemilik ? (
                        book.is_readable === 1 || book.is_readable === '1' ? (
                          <span className="katalog-public__book-badge katalog-public__book-badge--free">
                            <FiGlobe /> Akses Gratis
                          </span>
                        ) : (
                          <span className="katalog-public__book-badge katalog-public__book-badge--opensource">
                            <FiExternalLink /> Open Library
                          </span>
                        )
                      ) : book.sumber_buku === 'Upload Dosen' || book.id_pemilik ? (
                        <span className="katalog-public__book-badge katalog-public__book-badge--dosen">
                          <FiDownload /> Digital Dosen
                        </span>
                      ) : parseInt(book.stok_tersedia) > 0 ? (
                        <span className="katalog-public__book-badge katalog-public__book-badge--available">
                          Tersedia
                        </span>
                      ) : (
                        <span className="katalog-public__book-badge katalog-public__book-badge--borrowed">
                          Dipinjam
                        </span>
                      )}
                    </div>
                    <div className="katalog-public__book-info">
                      <h3 className="katalog-public__book-title">{book.judul}</h3>
                      <p className="katalog-public__book-author">{book.penulis || 'Unknown'}</p>
                      <div className="katalog-public__book-meta">
                        <span className="katalog-public__book-category">{book.nama_kategori || 'Umum'}</span>
                        <span className="katalog-public__book-year">{book.tahun_terbit || '-'}</span>
                      </div>
                      {viewMode === 'list' && book.deskripsi && (
                        <p className="katalog-public__book-desc">{book.deskripsi}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="katalog-public__pagination">
                  <button 
                    className="katalog-public__pagination-btn"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                  >
                    <FiChevronLeft />
                  </button>

                  <div className="katalog-public__pagination-pages">
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`katalog-public__pagination-page ${pageNum === pagination.page ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button 
                    className="katalog-public__pagination-btn"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.totalPages}
                  >
                    <FiChevronRight />
                  </button>
                </div>
              )}
            </>
          )}

          {/* Login CTA */}
          <div className="katalog-public__cta">
            <div className="katalog-public__cta-content">
              <h3>Ingin meminjam buku?</h3>
              <p>Silakan login untuk dapat meminjam buku dari perpustakaan</p>
            </div>
            <Link to="/login" className="katalog-public__cta-btn">
              <FiLogIn /> Login Sekarang
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="katalog-public__footer">
        <div className="katalog-public__footer-container">
          <p>&copy; 2024 STIKBAR E-Library. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default KatalogPublic;
